import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db, auth } from '../../../firebase/config';
import { sendInvoiceNotification } from '../../../services/notificationService';

export interface InvoiceManagement {
  id: string;
  invoiceNumber: string;
  date: Date | Timestamp;
  clientId: string;
  clientName: string;
  clientType: 'Individual' | 'Factory' | 'Bank';
  clientEmail?: string;
  serviceDescription: string;
  totalGuards: number;
  guardDeploymentAmount: number;
  totalAmount: number;
  status: 'pending' | 'recheck' | 'submitted' | 'sent' | 'paid' | 'overdue' | 'cancelled' | 'rejected';
  dueDate?: Date | Timestamp;
  sentDate?: Date | Timestamp;
  paidDate?: Date | Timestamp;
  paymentMethod?: string;
  notes?: string;
  rejectReason?: string;
  rejectedAt?: Date | Timestamp;
  rejectedBy?: string;
  recheckReason?: string;
  recheckedAt?: Date | Timestamp;
  recheckedBy?: string;
  approvedAt?: Date | Timestamp;
  approvedBy?: string;
  createdBy: string;
  createdAt: Date | Timestamp;
  updatedAt?: Date | Timestamp;
}

/**
 * Get all invoice management records
 */
export const fetchInvoiceManagement = async (companyId: string): Promise<InvoiceManagement[]> => {
  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const invoicesRef = collection(financeDocRef, 'invoiceManagement');
  const q = query(invoicesRef, orderBy('date', 'desc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      date: data.date?.toDate ? data.date.toDate() : new Date(data.date),
      dueDate: data.dueDate?.toDate ? data.dueDate.toDate() : data.dueDate ? new Date(data.dueDate) : undefined,
      sentDate: data.sentDate?.toDate ? data.sentDate.toDate() : data.sentDate ? new Date(data.sentDate) : undefined,
      paidDate: data.paidDate?.toDate ? data.paidDate.toDate() : data.paidDate ? new Date(data.paidDate) : undefined,
      rejectedAt: data.rejectedAt?.toDate ? data.rejectedAt.toDate() : data.rejectedAt ? new Date(data.rejectedAt) : undefined,
      recheckedAt: data.recheckedAt?.toDate ? data.recheckedAt.toDate() : data.recheckedAt ? new Date(data.recheckedAt) : undefined,
      approvedAt: data.approvedAt?.toDate ? data.approvedAt.toDate() : data.approvedAt ? new Date(data.approvedAt) : undefined,
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
      updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt ? new Date(data.updatedAt) : undefined,
    } as InvoiceManagement;
  });
};

/**
 * Add an invoice management record
 */
export const addInvoiceManagement = async (
  companyId: string,
  invoiceData: Omit<InvoiceManagement, 'id' | 'invoiceNumber' | 'createdBy' | 'createdAt' | 'updatedAt' | 'status'>
): Promise<string> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  // Generate invoice number if not provided
  const invoices = await fetchInvoiceManagement(companyId);
  const invoiceNumber = `INV-MGT-${String(invoices.length + 1).padStart(6, '0')}`;

  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const invoicesRef = collection(financeDocRef, 'invoiceManagement');
  const invoiceRef = doc(invoicesRef);

  let invoiceDate: Date;
  if (invoiceData.date instanceof Date) {
    invoiceDate = invoiceData.date;
  } else if (invoiceData.date && typeof (invoiceData.date as any).toDate === 'function') {
    invoiceDate = (invoiceData.date as any).toDate();
  } else if (invoiceData.date && typeof invoiceData.date === 'object' && (invoiceData.date as any).seconds) {
    const ts = invoiceData.date as Timestamp;
    invoiceDate = ts.toDate();
  } else {
    invoiceDate = new Date(invoiceData.date as any);
  }

  let dueDateObj: Date | undefined;
  if (invoiceData.dueDate) {
    if (invoiceData.dueDate instanceof Date) {
      dueDateObj = invoiceData.dueDate;
    } else if (typeof (invoiceData.dueDate as any).toDate === 'function') {
      dueDateObj = (invoiceData.dueDate as any).toDate();
    } else if (typeof invoiceData.dueDate === 'object' && (invoiceData.dueDate as any).seconds) {
      const ts = invoiceData.dueDate as Timestamp;
      dueDateObj = ts.toDate();
    } else {
      dueDateObj = new Date(invoiceData.dueDate as any);
    }
  }

  const invoice: any = {
    companyId,
    invoiceNumber,
    clientId: invoiceData.clientId,
    clientName: invoiceData.clientName,
    clientType: invoiceData.clientType,
    clientEmail: invoiceData.clientEmail || null,
    serviceDescription: invoiceData.serviceDescription,
    totalGuards: invoiceData.totalGuards,
    guardDeploymentAmount: invoiceData.guardDeploymentAmount,
    totalAmount: invoiceData.totalAmount,
    status: 'pending', // Default status is pending
    notes: invoiceData.notes || null,
    createdBy: user.uid,
    createdAt: serverTimestamp(),
    date: Timestamp.fromDate(invoiceDate),
  };

  if (dueDateObj) {
    invoice.dueDate = Timestamp.fromDate(dueDateObj);
  }

  await setDoc(invoiceRef, invoice);

  // Send FCM notification to company
  try {
    await sendInvoiceNotification(companyId, invoiceNumber, 'created', {
      clientName: invoiceData.clientName,
      totalAmount: invoiceData.totalAmount,
      status: 'pending',
    });
  } catch (error: any) {
    // Silent fail - notification failure shouldn't break invoice creation
  }

  return invoiceRef.id;
};

/**
 * Update invoice management
 */
export const updateInvoiceManagement = async (
  companyId: string,
  invoiceId: string,
  invoiceData: Partial<Omit<InvoiceManagement, 'id' | 'invoiceNumber' | 'createdBy' | 'createdAt' | 'status'>>
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const invoiceRef = doc(financeDocRef, 'invoiceManagement', invoiceId);

  const updateData: any = {
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
    status: 'pending', // Reset to pending after edit
  };

  // Only add fields that are provided and not undefined
  if (invoiceData.clientId !== undefined) updateData.clientId = invoiceData.clientId;
  if (invoiceData.clientName !== undefined) updateData.clientName = invoiceData.clientName;
  if (invoiceData.clientType !== undefined) updateData.clientType = invoiceData.clientType;
  if (invoiceData.clientEmail !== undefined && invoiceData.clientEmail !== null && invoiceData.clientEmail !== '') {
    updateData.clientEmail = invoiceData.clientEmail;
  }
  if (invoiceData.serviceDescription !== undefined) updateData.serviceDescription = invoiceData.serviceDescription;
  if (invoiceData.totalGuards !== undefined) updateData.totalGuards = invoiceData.totalGuards;
  if (invoiceData.guardDeploymentAmount !== undefined) updateData.guardDeploymentAmount = invoiceData.guardDeploymentAmount;
  if (invoiceData.totalAmount !== undefined) updateData.totalAmount = invoiceData.totalAmount;
  if (invoiceData.notes !== undefined && invoiceData.notes !== null && invoiceData.notes !== '') {
    updateData.notes = invoiceData.notes;
  }

  // Handle date conversions
  if (invoiceData.date) {
    let invoiceDate: Date;
    if (invoiceData.date instanceof Date) {
      invoiceDate = invoiceData.date;
    } else if ((invoiceData.date as any).toDate && typeof (invoiceData.date as any).toDate === 'function') {
      invoiceDate = (invoiceData.date as any).toDate();
    } else if ((invoiceData.date as any).seconds) {
      invoiceDate = new Date((invoiceData.date as any).seconds * 1000);
    } else {
      invoiceDate = new Date(invoiceData.date as any);
    }
    updateData.date = Timestamp.fromDate(invoiceDate);
  }

  if (invoiceData.dueDate) {
    let dueDateObj: Date;
    if (invoiceData.dueDate instanceof Date) {
      dueDateObj = invoiceData.dueDate;
    } else if ((invoiceData.dueDate as any).toDate && typeof (invoiceData.dueDate as any).toDate === 'function') {
      dueDateObj = (invoiceData.dueDate as any).toDate();
    } else if ((invoiceData.dueDate as any).seconds) {
      dueDateObj = new Date((invoiceData.dueDate as any).seconds * 1000);
    } else {
      dueDateObj = new Date(invoiceData.dueDate as any);
    }
    updateData.dueDate = Timestamp.fromDate(dueDateObj);
  } else if (invoiceData.dueDate === null) {
    // Explicitly set to null if provided as null (to clear the field)
    updateData.dueDate = null;
  }

  await updateDoc(invoiceRef, updateData);

  // Send FCM notification to company
  try {
    // Get invoice data to include in notification
    const invoiceDoc = await getDoc(invoiceRef);
    const invoiceData = invoiceDoc.data();

    await sendInvoiceNotification(companyId, invoiceData?.invoiceNumber || invoiceId, 'updated', {
      clientName: invoiceData?.clientName,
      totalAmount: invoiceData?.totalAmount,
      status: invoiceData?.status || updateData.status,
    });
  } catch (error) {
    // Silent fail - notification failure shouldn't break invoice update
    // Don't throw - notification failure shouldn't break invoice update
  }
};

/**
 * Approve invoice (change status to submitted)
 */
export const approveInvoice = async (
  companyId: string,
  invoiceId: string
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const invoiceRef = doc(financeDocRef, 'invoiceManagement', invoiceId);

  await updateDoc(invoiceRef, {
    status: 'submitted',
    approvedAt: serverTimestamp(),
    approvedBy: user.uid,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
  });
};

/**
 * Reject invoice (change status to rejected with reason)
 */
export const rejectInvoice = async (
  companyId: string,
  invoiceId: string,
  reason: string
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const invoiceRef = doc(financeDocRef, 'invoiceManagement', invoiceId);

  await updateDoc(invoiceRef, {
    status: 'rejected',
    rejectReason: reason,
    rejectedAt: serverTimestamp(),
    rejectedBy: user.uid,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
  });
};

/**
 * Recheck invoice (change status to recheck)
 */
export const recheckInvoice = async (
  companyId: string,
  invoiceId: string,
  reason: string
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const invoiceRef = doc(financeDocRef, 'invoiceManagement', invoiceId);

  await updateDoc(invoiceRef, {
    status: 'recheck',
    recheckReason: reason,
    recheckedAt: serverTimestamp(),
    recheckedBy: user.uid,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
  });
};

/**
 * Get invoice management by ID
 */
export const getInvoiceManagementById = async (
  companyId: string,
  invoiceId: string
): Promise<InvoiceManagement | null> => {
  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const invoiceRef = doc(financeDocRef, 'invoiceManagement', invoiceId);
  const invoiceDoc = await getDoc(invoiceRef);

  if (!invoiceDoc.exists()) {
    return null;
  }

  const data = invoiceDoc.data();
  return {
    id: invoiceDoc.id,
    ...data,
    date: data.date?.toDate ? data.date.toDate() : new Date(data.date),
    dueDate: data.dueDate?.toDate ? data.dueDate.toDate() : data.dueDate ? new Date(data.dueDate) : undefined,
    sentDate: data.sentDate?.toDate ? data.sentDate.toDate() : data.sentDate ? new Date(data.sentDate) : undefined,
    paidDate: data.paidDate?.toDate ? data.paidDate.toDate() : data.paidDate ? new Date(data.paidDate) : undefined,
    createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
    updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt ? new Date(data.updatedAt) : undefined,
  } as InvoiceManagement;
};

/**
 * Delete invoice management
 */
export const deleteInvoiceManagement = async (
  companyId: string,
  invoiceId: string
): Promise<void> => {
  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const invoiceRef = doc(financeDocRef, 'invoiceManagement', invoiceId);
  await deleteDoc(invoiceRef);
};

